import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Check, Circle, Clock } from "lucide-react";
import { api, getUser, clearAuth } from "../lib/api";
import Logo from "../components/Logo";

const statusOrder = ["discovery", "design", "build", "launch", "live"];

const StatusIcon = ({ s }) => {
  if (s === "done") return <Check size={14} className="text-[#C5A059]"/>;
  if (s === "in_progress") return <Clock size={14} className="text-[#1A1A1A]"/>;
  return <Circle size={14} className="text-[#D1CDC0]"/>;
};

export default function PortalDashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/projects").then((r) => setProjects(r.data.projects || [])).finally(() => setLoading(false));
  }, []);

  const logout = () => { clearAuth(); nav("/portal/login"); };

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <header className="bg-white border-b border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4" data-testid="portal-logo">
            <Logo height={42} />
            <span className="label-eyebrow border-l border-[#EBE8DF] pl-4">Client Portal</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-sm text-[#5C5C5C] hidden md:inline">{user?.email}</span>
            <button onClick={logout} data-testid="portal-logout" className="btn-secondary py-2 px-4 text-sm"><LogOut size={14}/> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="label-eyebrow">Welcome back</p>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">{user?.name || "Client"}</h1>
        <p className="mt-3 text-[#5C5C5C]">Here's where every project of yours stands today.</p>

        {loading && <p className="mt-12 text-[#8B8C89]">Loading projects…</p>}
        {!loading && projects.length === 0 && (
          <div className="mt-12 border border-[#EBE8DF] bg-white p-10" data-testid="portal-empty">
            <p className="font-display text-2xl">No projects yet.</p>
            <p className="mt-2 text-[#5C5C5C]">Once we kick off, your project journey will appear here.</p>
          </div>
        )}

        <div className="mt-12 space-y-10">
          {projects.map((p) => {
            const stageIdx = statusOrder.indexOf(p.status);
            return (
              <div key={p.id} data-testid={`portal-project-${p.id}`} className="border border-[#EBE8DF] bg-white">
                <div className="p-8 md:p-10 border-b border-[#EBE8DF]">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="label-eyebrow">Project</p>
                      <h2 className="font-display text-3xl mt-2">{p.title}</h2>
                      <p className="mt-2 text-[#5C5C5C] max-w-2xl">{p.description}</p>
                    </div>
                    <span className="bg-[#F5F3ED] text-[#1A1A1A] px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-bold">{p.status}</span>
                  </div>

                  {/* Stage track */}
                  <div className="mt-8 grid grid-cols-5 gap-2">
                    {statusOrder.map((s, i) => (
                      <div key={s} className="space-y-2">
                        <div className={`h-1.5 ${i <= stageIdx ? "bg-[#C5A059]" : "bg-[#EBE8DF]"}`}/>
                        <p className={`text-[11px] uppercase tracking-wider ${i <= stageIdx ? "text-[#1A1A1A]" : "text-[#B8B5AD]"}`}>{s}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#EBE8DF]">
                  <div className="p-8 md:p-10">
                    <p className="label-eyebrow">Milestones</p>
                    <ul className="mt-5 space-y-4">
                      {(p.milestones || []).map((m, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-1.5"><StatusIcon s={m.status}/></span>
                          <div>
                            <p className={`font-medium ${m.status === "done" ? "text-[#8B8C89] line-through" : "text-[#1A1A1A]"}`}>{m.title}</p>
                            {m.description && <p className="text-sm text-[#5C5C5C] mt-1">{m.description}</p>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-8 md:p-10">
                    <p className="label-eyebrow">Invoices</p>
                    <ul className="mt-5 space-y-3">
                      {(p.invoices || []).map((inv) => (
                        <li key={inv.id} className="flex items-center justify-between py-3 border-b border-[#EBE8DF] last:border-0">
                          <div>
                            <p className="font-medium">{inv.label}</p>
                            <p className="text-xs text-[#8B8C89] mt-0.5">{new Date(inv.issued).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-display text-xl">${inv.amount.toLocaleString()}</p>
                            <p className={`text-[11px] uppercase tracking-[0.2em] font-bold ${inv.status === "paid" ? "text-[#C5A059]" : "text-[#1A1A1A]"}`}>{inv.status}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
